import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../services/API'
import S3DocumentUploadDisplay from '../../components/images/S3DocumentUploadDisplay'
import { FormSection, YesNoQuestion } from './FormKit'
import { isValidLicenceTail, normaliseLicenceTail } from './applicationHistory'

// Mimics the licence number line on a photocard (field 5) with the part we
// ask for highlighted, so applicants can see exactly which characters to copy.
const LicenceNumberHint = () => (
  <Box
    aria-hidden
    sx={{
      display: 'inline-flex',
      fontFamily: 'monospace',
      fontSize: '0.95rem',
      letterSpacing: '0.12em',
      borderRadius: 1,
      overflow: 'hidden',
      border: 1,
      borderColor: 'divider',
    }}
  >
    <Box px={1} py={0.5} color="text.disabled" bgcolor="anMuted.main">
      MORGA753
    </Box>
    <Box px={1} py={0.5} fontWeight={700} bgcolor="#FFF4C2" color="text.primary">
      116SM9IJ
    </Box>
  </Box>
)

const Driving = ({ formValues, setFormValues }) => {
  const driving = formValues?.driving ?? {}
  const licenceTail = driving.licenceLastEight ?? ''
  const tailInvalid = licenceTail.length > 0 && !isValidLicenceTail(licenceTail)

  const setDriving = (patch) =>
    setFormValues((prev) => ({
      ...prev,
      driving: { ...prev.driving, ...patch },
    }))

  // Uploads save straight away so a document is never orphaned in S3 if the
  // applicant leaves without pressing save.
  const saveDocuments = (documents) => {
    setDriving({ documents })
    API.post('/update-employee-own-application', {
      _id: formValues._id,
      driving: { ...formValues.driving, documents },
    })
  }

  return (
    <Stack gap={2}>
      <FormSection title="Driving licence">
        <YesNoQuestion
          label="Do you hold a valid UK driving licence?"
          value={driving.hasValidLicence}
          onChange={(hasValidLicence) => setDriving({ hasValidLicence })}
        />
      </FormSection>

      {driving.hasValidLicence && (
        <>
          <FormSection
            title="Licence check"
            caption="We check every driver's record with the DVLA. To do that we need the last 8 characters of your licence number and a check code."
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack gap={1.5}>
                  <TextField
                    label="Last 8 characters of licence number"
                    required
                    fullWidth
                    autoComplete="off"
                    value={licenceTail}
                    onChange={(e) =>
                      setDriving({
                        licenceLastEight: normaliseLicenceTail(e.target.value),
                      })
                    }
                    error={tailInvalid}
                    helperText={
                      tailInvalid
                        ? 'Enter all 8 letters and numbers'
                        : 'Pasting your full licence number is fine — we only keep the last 8.'
                    }
                    inputProps={{
                      style: {
                        fontFamily: 'monospace',
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                      },
                    }}
                  />
                  <Box>
                    <Typography variant="body2" mb={0.75}>
                      It's in section 5 of your photocard licence — the
                      highlighted part:
                    </Typography>
                    <LicenceNumberHint />
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack gap={1.5}>
                  <TextField
                    label="DVLA check code"
                    required
                    fullWidth
                    autoComplete="off"
                    name="drivinglicence-check-code"
                    value={driving.checkCode ?? ''}
                    onChange={(e) => setDriving({ checkCode: e.target.value })}
                  />
                  <Typography variant="body2">
                    Get a check code from{' '}
                    <Link
                      href="https://www.gov.uk/view-driving-licence"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      GOV.UK — View or share your driving licence information
                    </Link>
                    . Codes last 21 days, so please don't generate one until
                    you're ready to submit.
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title="Entitlements">
            <Stack gap={2.5}>
              <YesNoQuestion
                label="Do you hold C1 entitlement?"
                helper="Needed to drive vehicles between 3.5 and 7.5 tonnes."
                value={driving.hasC1}
                onChange={(hasC1) => setDriving({ hasC1 })}
              />
              <YesNoQuestion
                label="Do you hold a blue light driving qualification?"
                value={driving.hasBlueLight}
                onChange={(hasBlueLight) => setDriving({ hasBlueLight })}
              />
            </Stack>
          </FormSection>

          <FormSection
            title="Documents"
            caption={
              driving.hasBlueLight
                ? 'Upload a copy of your driving licence (front and back) and your blue light certificate.'
                : 'Upload a copy of your driving licence (front and back).'
            }
          >
            <S3DocumentUploadDisplay
              existingDocuments={driving.documents}
              s3folder="employees/application-driving-documents"
              actionAfterSubmit={(newDocKey) =>
                saveDocuments([...(driving.documents ?? []), newDocKey])
              }
              actionAfterDelete={(newDocsArray) => saveDocuments(newDocsArray)}
            />
          </FormSection>
        </>
      )}

      {driving.hasValidLicence === false && (
        <Alert severity="info">
          No problem — some roles don't involve driving. We'll discuss this
          with you if it's needed for the role you're applying for.
        </Alert>
      )}
    </Stack>
  )
}

export default Driving
